import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Sparkles, Star, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const generateSessionId = () =>
  'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [started, setStarted] = useState(false);
  const [sending, setSending] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionIdRef = useRef(generateSessionId());
  const lastActivityRef = useRef(Date.now());
  const inactivityTimerRef = useRef(null);

  // Show widget after 3s
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch messages when chat is open and started
  useEffect(() => {
    if (open && started) fetchMessages();
  }, [open, started]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages
  useEffect(() => {
    if (!open || !started) return;
    const interval = setInterval(fetchMessages, 8000);
    return () => clearInterval(interval);
  }, [open, started]);

  // Inactivity auto-reset (12 minutes)
  useEffect(() => {
    if (!started) return;
    const checkInactivity = () => {
      if (Date.now() - lastActivityRef.current > 12 * 60 * 1000) {
        handleEndSession(true);
      }
    };
    inactivityTimerRef.current = setInterval(checkInactivity, 60000);
    return () => {
      if (inactivityTimerRef.current) clearInterval(inactivityTimerRef.current);
    };
  }, [started]);

  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/messages/${sessionIdRef.current}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch {}
  };

  const handleStart = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setStarted(true);
    lastActivityRef.current = Date.now();
    setMessages([{
      role: 'support',
      text: `Hola ${name.trim()}! Bienvenido a OG MUSIC. ¿En qué puedo ayudarte hoy?`,
      timestamp: new Date().toISOString()
    }]);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    trackActivity();
    setMessages(prev => [...prev, { role: 'user', text, timestamp: new Date().toISOString() }]);
    setSending(true);
    try {
      await fetch(`${API_URL}/api/chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          name,
          email: email || undefined,
          message: text
        })
      });
      setTimeout(fetchMessages, 1500);
    } catch {}
    setSending(false);
  };

  const handleEndSession = async (isAuto = false) => {
    if (!isAuto) {
      setShowRating(true);
    } else {
      // Auto-end: just reset without rating
      resetChat();
    }
  };

  const submitRating = async () => {
    try {
      await fetch(`${API_URL}/api/chat/end-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          rating: rating || null,
          comment: ratingComment,
          email,
          name,
        })
      });
    } catch {}
    setRatingSubmitted(true);
    setTimeout(() => resetChat(), 2000);
  };

  const skipRating = async () => {
    try {
      await fetch(`${API_URL}/api/chat/end-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionIdRef.current })
      });
    } catch {}
    resetChat();
  };

  const resetChat = () => {
    sessionIdRef.current = generateSessionId();
    setMessages([]);
    setStarted(false);
    setShowRating(false);
    setRating(0);
    setRatingHover(0);
    setRatingComment('');
    setRatingSubmitted(false);
    setInput('');
  };

  const handleClose = () => {
    setOpen(false);
    // Don't reset session on close — only on explicit end
  };

  const ratingLabels = {
    1: 'Terrible', 2: 'Muy malo', 3: 'Malo', 4: 'Regular', 5: 'Aceptable',
    6: 'Bien', 7: 'Bueno', 8: 'Muy bueno', 9: 'Excelente', 10: 'Perfecto'
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed right-4 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#E8720C] to-[#C55A00] text-white shadow-lg shadow-[#E8720C]/30 hover:shadow-[#E8720C]/50 flex items-center justify-center transition-all duration-500 hover:scale-110 ${visible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
        style={{ bottom: '80px' }}
        data-testid="chat-widget-button"
        aria-label="Open support chat"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed right-4 z-50 w-[360px] max-w-[calc(100vw-32px)] bg-[#0c0c10] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden flex flex-col"
          style={{ bottom: '102px', height: '500px' }}
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#E8720C] to-[#C55A00] px-5 py-3 flex items-center gap-3">
            <div className="w-9 h-9 bg-black/20 rounded-full flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm text-white">OG MUSIC Assistant</h3>
              <p className="text-[10px] text-white/80">Soporte y asistencia</p>
            </div>
            {started && !showRating && (
              <button
                onClick={() => handleEndSession(false)}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-black/20 rounded-lg text-[10px] text-white/80 hover:bg-black/30 transition-colors"
                data-testid="chat-end-session-btn"
                title="Finalizar conversación"
              >
                <LogOut className="w-3 h-3" />
                Finalizar
              </button>
            )}
          </div>

          {/* Rating Screen */}
          {showRating ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4" data-testid="chat-rating-screen">
              {ratingSubmitted ? (
                <div className="text-center">
                  <Star className="w-10 h-10 text-[#E8720C] mx-auto mb-3" />
                  <p className="text-white font-semibold mb-1">Gracias por tu feedback!</p>
                  <p className="text-white/40 text-xs">Tu opinión nos ayuda a mejorar.</p>
                </div>
              ) : (
                <>
                  <p className="text-white font-semibold text-center">¿Qué tan satisfecho estás?</p>
                  <p className="text-white/40 text-xs text-center">Tu opinión nos ayuda a mejorar nuestro servicio</p>
                  
                  {/* 1-10 Rating */}
                  <div className="flex gap-1 flex-wrap justify-center" data-testid="chat-rating-buttons">
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <button
                        key={n}
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setRatingHover(n)}
                        onMouseLeave={() => setRatingHover(0)}
                        className={`w-9 h-9 rounded-lg text-xs font-bold transition-all ${
                          n <= (ratingHover || rating)
                            ? n <= 5 ? 'bg-red-500/80 text-white' : 'bg-green-500/80 text-white'
                            : 'bg-white/5 text-white/30 border border-white/10'
                        }`}
                        data-testid={`chat-rating-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {(ratingHover || rating) > 0 && (
                    <p className="text-xs text-white/50">{ratingLabels[ratingHover || rating]}</p>
                  )}

                  {/* Comment */}
                  <textarea
                    value={ratingComment}
                    onChange={e => setRatingComment(e.target.value)}
                    placeholder="Comentario (opcional)"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50 resize-none"
                    rows={2}
                    data-testid="chat-rating-comment"
                  />

                  <div className="flex gap-2 w-full">
                    <button
                      onClick={skipRating}
                      className="flex-1 py-2.5 text-xs text-white/40 hover:text-white/60 transition-colors"
                      data-testid="chat-rating-skip"
                    >
                      Omitir
                    </button>
                    <button
                      onClick={submitRating}
                      disabled={!rating}
                      className="flex-1 py-2.5 bg-[#E8720C] text-white text-xs font-semibold rounded-xl hover:bg-[#C55A00] transition-colors disabled:opacity-40"
                      data-testid="chat-rating-submit"
                    >
                      Enviar
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : !started ? (
            /* Start Form */
            <form onSubmit={handleStart} className="flex-1 flex flex-col justify-center p-6 gap-4">
              <div className="text-center mb-2">
                <Sparkles className="w-8 h-8 text-[#E8720C] mx-auto mb-3" />
                <p className="text-sm text-white/70">
                  Estoy aquí para ayudarte. Pregúntame lo que necesites sobre nuestra música, compras o soporte.
                </p>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre *"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                required
                data-testid="chat-name-input"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (solo si necesitas ayuda con una orden)"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                data-testid="chat-email-input"
              />
              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-[#E8720C] to-[#C55A00] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity text-sm"
                data-testid="chat-start-btn"
              >
                Iniciar Conversación
              </button>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                      msg.role === 'user'
                        ? 'bg-[#E8720C] text-white rounded-br-md'
                        : 'bg-white/5 text-white/80 border border-white/5 rounded-bl-md'
                    }`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-white/5 border border-white/5 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-[#E8720C] rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                        <div className="w-2 h-2 bg-[#E8720C] rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                        <div className="w-2 h-2 bg-[#E8720C] rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSend} className="p-3 border-t border-white/5 flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => { setInput(e.target.value); trackActivity(); }}
                  placeholder="Escribe tu mensaje..."
                  className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                  data-testid="chat-message-input"
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  className="w-10 h-10 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl flex items-center justify-center text-white transition-colors disabled:opacity-40"
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
